import {
    CloudflareVideoModel,
    OtherCareerVideoModel,
    RoadtripNationVideoModel,
} from '@/apiTypes/VideoTypes';
import { getRoadtripNationNameAndTitleString } from '@/components/Video/VideoUtils';
import type { UnifiedVideoItem } from '@/components/Video/unifiedVideoTypes';

/**
 * Display names for each upstream source. Kept in one place so a rebrand is a config change
 * rather than a search across every component that renders a video.
 */
export const VIDEO_CREATOR_LABELS = {
    roadtripNation: 'Roadtrip Nation',
    futureNetwork: 'Future Network',
    careerOneStop: 'CareerOneStop',
    cloudflare: 'On the Job',
} as const;

export function getYoutubeThumbUrl(url: string): string | null {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match?.[1] ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
}

export function getCloudflareThumbUrl(externalVideoUid: string): string {
    return `https://videodelivery.net/${externalVideoUid}/thumbnails/thumbnail.jpg`;
}

export function toUnifiedVideoItems(
    roadtripNation: RoadtripNationVideoModel[],
    futureNetwork: OtherCareerVideoModel[],
    careerOneStop: OtherCareerVideoModel[],
    cloudflare: CloudflareVideoModel[],
    occupationName?: string,
): UnifiedVideoItem[] {
    const roadtripItems: UnifiedVideoItem[] = roadtripNation.map((v, i) => ({
        sourceType: 'roadtripNation',
        id: `roadtrip-${v.jwAssetId}-${i}`,
        storyId: v.storyId,
        thumbUrl: v.thumbUrl,
        title: getRoadtripNationNameAndTitleString(v) || 'Roadtrip Nation Interview',
        creator: VIDEO_CREATOR_LABELS.roadtripNation,
        data: v,
    }));

    const futureItems: UnifiedVideoItem[] = futureNetwork.map((v, i) => ({
        sourceType: 'other',
        id: `future-${v.id}-${i}`,
        storyId: v.storyId,
        thumbUrl: getYoutubeThumbUrl(v.videoUrl) ?? '',
        title: v.videoTitle ?? 'Career video',
        creator: VIDEO_CREATOR_LABELS.futureNetwork,
        data: v,
    }));

    const careerOneStopItems: UnifiedVideoItem[] = careerOneStop.map((v, i) => ({
        sourceType: 'other',
        id: `cos-${v.id}-${i}`,
        storyId: v.storyId,
        thumbUrl: getYoutubeThumbUrl(v.videoUrl) ?? '',
        title: v.videoTitle ?? 'Career video',
        creator: VIDEO_CREATOR_LABELS.careerOneStop,
        data: v,
    }));

    const cloudflareItems: UnifiedVideoItem[] = cloudflare.map((v, i) => ({
        sourceType: 'cloudflare',
        id: `cloudflare-${v.id}-${i}`,
        storyId: v.storyId,
        thumbUrl: getCloudflareThumbUrl(v.externalVideoUid),
        title: occupationName ?? 'Career summary video',
        creator: VIDEO_CREATOR_LABELS.cloudflare,
        data: v,
    }));

    return [...cloudflareItems, ...roadtripItems, ...futureItems, ...careerOneStopItems];
}
